import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  useColorModeValue,
  Badge,
  Tag,
  TagLabel,
  TagLeftIcon,
  Grid,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaChartLine,
  FaLightbulb,
  FaEye,
  FaKey,
} from 'react-icons/fa';
import { SectionLabel } from './DashboardWrapper';
import {
  hasCompetitiveIntel,
  getTopKeywordsByVolume,
  getKeywordOpportunities,
  getTotalOrganicKeywords,
  formatNumber as formatNum,
  getKeywordDifficultyColor,
} from '../../utils/competitiveIntelHelpers';

const KeywordsDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const businessesWithKeywords = useMemo(() => {
    return reviewsData.filter(business => hasCompetitiveIntel(business));
  }, [reviewsData]);

  const keywordStats = useMemo(() => {
    if (!businessesWithKeywords.length) return null;

    const totalKeywords = businessesWithKeywords.reduce((sum, business) => {
      return sum + getTotalOrganicKeywords(business);
    }, 0);

    const avgKeywords = Math.round(totalKeywords / businessesWithKeywords.length);

    const allTopKeywords = [];
    businessesWithKeywords.forEach(business => {
      const topKws = getTopKeywordsByVolume(business, 5);
      topKws.forEach(kw => {
        allTopKeywords.push({
          ...kw,
          businessName: business.name
        });
      });
    });

    const topKeywords = allTopKeywords
      .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
      .slice(0, 8);

    const allOpportunities = [];
    businessesWithKeywords.forEach(business => {
      const opps = getKeywordOpportunities(business, {
        minVolume: 100,
        maxDifficulty: 50,
        minRank: 11
      });
      opps.forEach(opp => {
        allOpportunities.push({
          ...opp,
          businessName: business.name
        });
      });
    });

    return {
      totalBusinesses: businessesWithKeywords.length,
      totalKeywords,
      avgKeywords,
      topKeywords,
      opportunities: allOpportunities.slice(0, 8)
    };
  }, [businessesWithKeywords]);

  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Keyword rankings and opportunities across your competitive landscape. Discover high-value keywords and untapped search opportunities.">
          Keywords
        </SectionLabel>}

        <Flex direction="column" align="center" justify="center" minH="400px" p={8}>
          <Icon as={FaKey} boxSize={16} color="gray.300" mb={4} />
          <Heading size="md" color="gray.500" mb={2}>No Keyword Data Available</Heading>
          <Text color="gray.400" textAlign="center">
            Keyword intelligence will appear here once competitive data is available for your market.
          </Text>
        </Flex>
      </Box>
    );
  }

  if (!keywordStats) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Keyword rankings and opportunities across your competitive landscape. Discover high-value keywords and untapped search opportunities.">
          Keywords
        </SectionLabel>}

        <Flex direction="column" align="center" justify="center" minH="400px" p={8}>
          <Icon as={FaSearch} boxSize={16} color="gray.300" mb={4} />
          <Heading size="md" color="gray.500" mb={2}>No Keyword Intelligence Found</Heading>
          <Text color="gray.400" textAlign="center">
            None of the businesses in your market have keyword ranking data yet.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      {!demoMode && <SectionLabel description="Keyword rankings and opportunities across your competitive landscape. Discover high-value keywords and untapped search opportunities.">
        Keywords
      </SectionLabel>}

      {/* Summary Stats */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4} mb={8}>
        <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={5}>
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="lg"
              bgGradient="linear(to-br, purple.400, purple.600)"
              justify="center"
              align="center"
            >
              <Icon as={FaKey} color="white" boxSize={4} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                {keywordStats.totalKeywords.toLocaleString()}
              </Text>
              <Text fontSize="xs" color={subtitleColor}>Total Keywords</Text>
            </VStack>
          </HStack>
        </Box>

        <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={5}>
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="lg"
              bgGradient="linear(to-br, blue.400, blue.600)"
              justify="center"
              align="center"
            >
              <Icon as={FaChartLine} color="white" boxSize={4} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                {keywordStats.avgKeywords.toLocaleString()}
              </Text>
              <Text fontSize="xs" color={subtitleColor}>Avg per Business</Text>
            </VStack>
          </HStack>
        </Box>

        <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={5}>
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="lg"
              bgGradient="linear(to-br, green.400, green.600)"
              justify="center"
              align="center"
            >
              <Icon as={FaLightbulb} color="white" boxSize={4} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                {keywordStats.opportunities.length}
              </Text>
              <Text fontSize="xs" color={subtitleColor}>Opportunities Found</Text>
            </VStack>
          </HStack>
        </Box>
      </SimpleGrid>

      {/* Keywords Content */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        {/* Top Keywords */}
        <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
          <HStack justify="space-between" mb={6}>
            <VStack align="start" spacing={1}>
              <HStack>
                <Icon as={FaChartLine} color="blue.500" />
                <Heading size="md" color={textColor}>Top Keywords</Heading>
              </HStack>
              <Text fontSize="sm" color={subtitleColor}>Highest search volume keywords</Text>
            </VStack>
            <Badge colorScheme="blue">{keywordStats.totalKeywords.toLocaleString()} total</Badge>
          </HStack>
          
          <VStack spacing={3} align="stretch">
            {keywordStats.topKeywords.map((kw, index) => (
              <HStack
                key={index}
                p={3}
                bg={statBg}
                borderRadius="lg"
                _hover={{ bg: hoverBg }}
                transition="all 0.2s"
              >
                <Badge colorScheme="purple" variant="solid" px={2}>
                  #{kw.rank || '-'}
                </Badge>
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontWeight="medium" color={textColor} fontSize="sm">{kw.keyword}</Text>
                  <Text fontSize="xs" color={subtitleColor}>{kw.businessName}</Text>
                </VStack>
                <HStack spacing={2}>
                  <Tag size="sm" colorScheme="green">
                    <TagLeftIcon as={FaEye} />
                    <TagLabel>{formatNum(kw.searchVolume || 0)}</TagLabel>
                  </Tag>
                  <Tag size="sm" colorScheme={getKeywordDifficultyColor(kw.keywordDifficulty || 0)}>
                    {kw.keywordDifficulty || 0}
                  </Tag>
                </HStack>
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* Keyword Opportunities */}
        <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
          <HStack justify="space-between" mb={6}>
            <VStack align="start" spacing={1}>
              <HStack>
                <Icon as={FaLightbulb} color="yellow.500" />
                <Heading size="md" color={textColor}>Opportunities</Heading>
              </HStack>
              <Text fontSize="sm" color={subtitleColor}>High volume, low difficulty keywords</Text>
            </VStack>
            <Badge colorScheme="green">{keywordStats.opportunities.length} found</Badge>
          </HStack>
          
          <VStack spacing={3} align="stretch">
            {keywordStats.opportunities.map((kw, index) => (
              <HStack
                key={index}
                p={3}
                bg="green.50"
                borderWidth="1px"
                borderColor="green.200"
                borderRadius="lg"
                _hover={{ bg: 'green.100' }}
                transition="all 0.2s"
              >
                <Badge colorScheme="orange" variant="solid" px={2}>
                  #{kw.rank || '-'}
                </Badge>
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontWeight="medium" color={textColor} fontSize="sm">{kw.keyword}</Text>
                  <Text fontSize="xs" color={subtitleColor}>{kw.businessName}</Text>
                </VStack>
                <HStack spacing={2}>
                  <Tag size="sm" colorScheme="green" variant="solid">
                    <TagLeftIcon as={FaEye} />
                    <TagLabel>{formatNum(kw.searchVolume || 0)}</TagLabel>
                  </Tag>
                  <Tag size="sm" colorScheme="green" variant="outline">
                    {kw.keywordDifficulty || 0} diff
                  </Tag>
                </HStack>
              </HStack>
            ))}
            
            {keywordStats.opportunities.length === 0 && (
              <Flex direction="column" align="center" py={8}>
                <Icon as={FaSearch} color="gray.300" boxSize={10} mb={3} />
                <Text color={subtitleColor}>No keyword opportunities found</Text>
              </Flex>
            )}
          </VStack>
        </Box>
      </Grid>
    </Box>
  );
};

export default KeywordsDashboard;
